import { Box, Flex, Text, chakra } from '@chakra-ui/react';
import React from 'react';

import type { Route } from 'nextjs-routes';
import { route } from 'nextjs-routes';

import { Link } from 'toolkit/chakra/link';
import { Skeleton } from 'toolkit/chakra/skeleton';
import { Hint } from 'toolkit/components/Hint/Hint';
import IconSvg, { type IconName } from 'ui/shared/IconSvg';
import TruncatedValue from 'ui/shared/TruncatedValue';

export type Props = {
  isImage?: boolean;
  className?: string;
  label: string;
  value: string | React.ReactNode;
  valuePrefix?: string;
  valuePostfix?: string;
  hint?: string | React.ReactNode;
  isLoading?: boolean;
  diff?: string | number;
  diffFormatted?: string;
  diffPeriod?: '24h';
  period?: '1h' | '24h' | '30min';
  href?: Route;
  icon?: IconName | React.ReactNode;
  index?: number;
  totalItemsCount?: number;
  isHomePage?: boolean;
};

const Container = ({ href, children, className }: { href?: Route; children: React.JSX.Element; className?: string }) => {
  if (href) {
    return (
      <Link href={ route(href) } variant="plain" className={ className }>
        { children }
      </Link>
    );
  }

  return children;
};

const StatsWidget = ({
  index,
  totalItemsCount,
  className,
  icon,
  isImage = false,
  label,
  value,
  valuePrefix,
  valuePostfix,
  isLoading,
  hint,
  diff,
  diffPeriod = '24h',
  diffFormatted,
  period,
  href,
  isHomePage = false,
}: Props) => {
  const isOnTopRow = index !== undefined && totalItemsCount !== undefined && index < totalItemsCount / 2;
  const isLeftColumn = index !== undefined && index % 2 === 0;

  return (
    <Container href={ !isLoading ? href : undefined } className={ href ? className : undefined }>
      <Flex
        className={ href ? undefined : className }
        alignItems="flex-start"
        borderRadius={ isHomePage ? 'none' : 'md' }
        bgColor={ isHomePage ? 'transparent' : { _light: 'white', _dark: 'black' } }
        border={ isHomePage ? 'none' : '1px solid' }
        { ...(isHomePage ? { borderBottom: isOnTopRow ? '0.6px solid' : 'none', borderRight: isLeftColumn ? '0.6px solid' : 'none' } : {}) }
        p={{ base: 1, lg: isHomePage ? 2 : 5 }}
        borderColor={{ _light: '#E0E0E0', _dark: '#2D2D2D' }}
        justifyContent="flex-start"
        columnGap={ 3 }
        w="100%"
        h="100%"
        minH="40px"
      >
        { icon && typeof icon === 'string' ? (
          <IconSvg
            name={ icon as IconName }
            isImage={ isImage }
            p={ 2 }
            boxSize={{ base: '32px', lg: '40px' }}
            isLoading={ isLoading }
            borderRadius="base"
            display={{ base: 'none', lg: 'block' }}
            flexShrink={ 0 }
          />
        ) : icon as React.ReactNode }
        <Box w={{ base: '100%', lg: icon ? 'calc(100% - 56px)' : '100%' }}>
          <Skeleton
            loading={ isLoading }
            color="text.secondary"
            textStyle="xs"
            w="fit-content"
          >
            <h2>{ label }</h2>
          </Skeleton>
          <Skeleton
            loading={ isLoading }
            display="flex"
            alignItems="baseline"
            fontWeight={ 500 }
            textStyle="heading.md"
          >
            { valuePrefix && <chakra.span whiteSpace="pre">{ valuePrefix }</chakra.span> }
            { typeof value === 'string' ? (
              <TruncatedValue isLoading={ isLoading } value={ value }/>
            ) : (
              value
            ) }
            { valuePostfix && <chakra.span whiteSpace="pre">{ valuePostfix }</chakra.span> }
            { diff && Number(diff) > 0 && (
              <>
                <Text ml={ 2 } mr={ 1 } color="green.500">
                  +{ diffFormatted || Number(diff).toLocaleString() }
                </Text>
                <Text color="text.secondary" textStyle="sm">({ diffPeriod })</Text>
              </>
            ) }
            { period && <Text color="text.secondary" textStyle="xs" fontWeight={ 400 } ml={ 1 }>({ period })</Text> }
          </Skeleton>
        </Box>
        { typeof hint === 'string' ? (
          <Skeleton loading={ isLoading } alignSelf="center" borderRadius="base">
            <Hint label={ hint } boxSize={ 5 } color="icon.secondary"/>
          </Skeleton>
        ) : hint }
      </Flex>
    </Container>
  );
};

export default chakra(StatsWidget);
